import {languageNames} from 'src/app/training/languageUtils';
import {MONTH_NAMES, RENEWAL_SCHEDULE} from 'src/app/training/courseDefaults';
import type {IFormDataV2} from 'src/types/FormV2';

export interface CourseSettingsSummaryChip {
  /** Settings group this chip belongs to, for expand-and-scroll. */
  group: string;
  label: string;
  /** Set when the chip reports an unconfigured / off state. */
  muted?: boolean;
}

const asNumber = (value: unknown): number | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const isOn = (value: unknown): boolean => value === true || value === 'true';

// Slice, not a Date: the fixed month/day carry no timezone.
const shortMonthName = (month: number): string =>
  MONTH_NAMES[month - 1]?.slice(0, 3) ?? '';

/**
 * Condenses the course settings values into the chip row shown on the
 * settings section's collapsed control, so the configuration stays legible
 * without expanding the panel.
 *
 * The course name is deliberately absent — `PageHeader.Title` already renders
 * it. `projectDisplayValue` comes from the form's display values because the
 * stored value is an id.
 */
export function buildCourseSettingsSummary(
  values: IFormDataV2,
  projectDisplayValue?: string
): CourseSettingsSummaryChip[] {
  const chips: CourseSettingsSummaryChip[] = [];

  chips.push(
    values.projectId
      ? {group: 'Identity', label: projectDisplayValue || 'Single project'}
      : {group: 'Identity', label: 'All projects', muted: true}
  );

  const language = values.defaultLanguage as string | undefined;
  if (language) {
    chips.push({
      group: 'Identity',
      label: languageNames[language] ?? language,
    });
  }

  chips.push(
    isOn(values.active)
      ? {group: 'Identity', label: 'Active'}
      : {group: 'Identity', label: 'Inactive', muted: true}
  );

  const dependencies = values.targetDependencies;
  const dependencyCount = Array.isArray(dependencies) ? dependencies.length : 0;
  if (dependencyCount > 0) {
    chips.push({
      group: 'Identity',
      label: `${dependencyCount} prerequisite${
        dependencyCount === 1 ? '' : 's'
      }`,
    });
  }

  const dueInDays = asNumber(values.dueInDays);
  chips.push(
    dueInDays
      ? {group: 'Deadlines', label: `Due ${dueInDays}d`}
      : {group: 'Deadlines', label: 'No due date', muted: true}
  );

  const renewalPeriodDays = asNumber(values.renewalPeriodDays);
  const fixedRenewalMonth = asNumber(values.fixedRenewalMonth);
  const fixedRenewalDay = asNumber(values.fixedRenewalDay);
  if (isOn(values.isRenewalRequired)) {
    if (values.renewalScheduleType === RENEWAL_SCHEDULE.FIXED) {
      chips.push(
        fixedRenewalMonth && fixedRenewalDay
          ? {
              group: 'Deadlines',
              label: `Renews annually on ${shortMonthName(
                fixedRenewalMonth
              )} ${fixedRenewalDay}`,
            }
          : {group: 'Deadlines', label: 'Renews annually (no date)'}
      );
    } else {
      chips.push(
        renewalPeriodDays
          ? {group: 'Deadlines', label: `Renews ${renewalPeriodDays}d`}
          : {group: 'Deadlines', label: 'Renews (no period)'}
      );
    }
  } else {
    chips.push({group: 'Deadlines', label: 'No renewal', muted: true});
  }

  const attemptLimit = asNumber(values.attemptLimit);
  const lockoutDays = asNumber(values.attemptLockoutPeriodDays);
  if (isOn(values.isAttemptLimited)) {
    if (attemptLimit) {
      chips.push({
        group: 'Attempts',
        label: lockoutDays
          ? `${attemptLimit} attempts / ${lockoutDays}d`
          : `${attemptLimit} attempts`,
      });
    } else {
      chips.push({group: 'Attempts', label: 'Limited (no attempt count)'});
    }
  } else {
    chips.push({group: 'Attempts', label: 'Unlimited attempts', muted: true});
  }

  chips.push(
    isOn(values.isCertificateGenerated)
      ? {group: 'Certificate', label: 'Certificate on'}
      : {group: 'Certificate', label: 'No certificate', muted: true}
  );

  return chips;
}
